import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { ChangePasswordService } from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@services/auth.service';
import * as Sentry from '@sentry/angular';

@Component({
  standalone: false,
  selector: 'wr-change-user-password-dialog',
  templateUrl: './change-user-password-dialog.component.html',
  styleUrls: ['./change-user-password-dialog.component.scss'],
})
export class ChangeUserPasswordDialogComponent implements OnInit {
  private readonly changePasswordService = inject(ChangePasswordService);
  private readonly authService = inject(AuthService);
  private readonly dialogRef = inject(NbDialogRef<ChangeUserPasswordDialogComponent>);

  @Input() mode: 'own' | 'admin' = 'own';
  @Input() userId = '';

  readonly submitting = signal(false);
  readonly serverError = signal('');
  form!: FormGroup;

  ngOnInit(): void {
    this.form = new FormGroup({
      ...(this.mode === 'own' ? { currentPassword: new FormControl('', [Validators.required]) } : {}),
      newPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
      repeatPassword: new FormControl('', [Validators.required]),
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  async submit(): Promise<void> {
    if (this.form.invalid) return;

    if (this.form.value.newPassword !== this.form.value.repeatPassword) {
      this.serverError.set('Passwörter stimmen nicht überein.');
      return;
    }

    if (this.mode === 'own') {
      await this.submitOwn();
    } else {
      await this.submitAdmin();
    }
  }

  private async submitOwn(): Promise<void> {
    this.submitting.set(true);
    this.serverError.set('');
    try {
      const result = await firstValueFrom(this.changePasswordService.changeOwnPassword({
        currentPassword: this.form.value.currentPassword ?? '',
        newPassword: this.form.value.newPassword ?? '',
        repeatPassword: this.form.value.repeatPassword ?? '',
      }));

      if (!result.success) {
        this.serverError.set(result.message ?? 'Ein Fehler ist aufgetreten.');
        return;
      }

      // Der Wechsel widerruft alle Sitzungen des Benutzers (WR-2385); diese hier bekommt ein
      // frisches Token und bleibt angemeldet. Ohne Token wäre das im Browser tot - dann abmelden.
      if (!result.token) {
        this.hardLogout();
        return;
      }
      this.authService.adoptRotatedToken(result.token);
      this.dialogRef.close(true);
    } catch {
      this.serverError.set('Ein Fehler ist aufgetreten. Bitte erneut versuchen.');
    } finally {
      this.submitting.set(false);
    }
  }

  private async submitAdmin(): Promise<void> {
    if (!this.userId) {
      this.serverError.set('Kein Benutzer ausgewählt.');
      return;
    }

    this.submitting.set(true);
    this.serverError.set('');
    try {
      const result = await firstValueFrom(this.changePasswordService.changeUserPassword({
        userId: this.userId,
        newPassword: this.form.value.newPassword ?? '',
        repeatPassword: this.form.value.repeatPassword ?? '',
      }));

      if (!result.success) {
        this.serverError.set(result.message ?? 'Ein Fehler ist aufgetreten.');
        return;
      }

      if (this.userId === this.authService.authCredentials()?.id) {
        this.hardLogout();
        return;
      }

      this.dialogRef.close(true);
    } catch {
      this.serverError.set('Ein Fehler ist aufgetreten. Bitte erneut versuchen.');
    } finally {
      this.submitting.set(false);
    }
  }

  private hardLogout(): void {
    localStorage.removeItem('wr-jwt');
    Sentry.setUser(null);
    window.location.href = '/login';
  }
}
